import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Circle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { dateShort, dateTime, money } from "@/lib/format";
import { DocumentEditor, type EditorDoc } from "@/modules/documents/DocumentEditor";
import { ORDER_STATUS_LABEL, ORDER_STATUS_VARIANT } from "./OrdersPage";
import { DeliveriesSection } from "./DeliveriesSection";
import { OrderBillingSection } from "@/modules/invoices/OrderBillingSection";
import { OrderContractSection } from "@/modules/contracts/OrderContractSection";
import { DeleteButton } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: ["order", id], enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase.from("orders").select("*, clients(id, legal_name, status, delivery_channel), quotes!orders_quote_id_fkey(id, number, version), order_items(*), order_fees(*)").eq("id", id!).single();
      if (error) throw error;
      data.order_items.sort((a, b) => a.position - b.position);
      return data;
    },
  });
}

/** Une étape de la validation de la vente (au niveau du module : sinon React la recrée à chaque rendu). */
function Step({ done, label, detail }: { done: boolean; label: string; detail?: string }) {
  return (
    <li className="flex items-start gap-2 text-sm">{done ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> : <Circle className="mt-0.5 h-4 w-4 text-muted-foreground" />}<div><div className={done ? "font-medium" : "text-muted-foreground"}>{label}</div>{detail && <div className="text-xs text-muted-foreground">{detail}</div>}</div></li>
  );
}

export default function OrderPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: o, isLoading } = useOrder(id);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [note, setNote] = useState("");
  const update = useMutation({
    mutationFn: async (values: Record<string, unknown>) => { const { error } = await supabase.from("orders").update(values as never).eq("id", id!); if (error) throw error; },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["order", id] }); void qc.invalidateQueries({ queryKey: ["orders"] }); },
    onError: (e) => { toast.error("Action impossible."); logger.error("order.update", e); },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (!o) return <p className="text-sm text-destructive">Commande introuvable.</p>;

  const doc: EditorDoc = {
    currency: o.currency,
    lines: o.order_items.map((it) => ({ key: it.id, product_id: it.product_id, sku: it.sku, label: it.label, description: it.description ?? "", quantity: Number(it.quantity), unit_price: Number(it.unit_price), catalog_price: null, price_source: "manual", discount_type: it.discount_type, discount_value: it.discount_value === null ? null : Number(it.discount_value) })),
    fees: o.order_fees.map((f) => ({ key: f.id, fee_type_id: f.fee_type_id, label: f.label, amount: Number(f.amount) })),
    global_discount_type: o.global_discount_type, global_discount_value: o.global_discount_value === null ? null : Number(o.global_discount_value),
    tax_rate_pct: Number(o.tax_rate_pct), tax_label: o.tax_label, deposit_pct: Number(o.deposit_pct),
  };
  const quote = o.quotes as { id: string; number: string | null; version: number } | null;

  return (
    <div>
      <Link to="/commandes" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Commandes</Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-bold">{o.number}</h1>
            <Badge variant={ORDER_STATUS_VARIANT[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge>
            {["confirmed", "in_delivery", "delivered", "closed"].includes(o.status) && (
              <Badge variant="outline" data-testid="delivery-progress">{o.order_items.reduce((s, it) => s + Number(it.quantity_delivered), 0)} / {o.order_items.reduce((s, it) => s + Number(it.quantity), 0)} livrés</Badge>
            )}
            {o.delivery_suspended && <Badge variant="destructive">Livraisons suspendues</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            <Link to={`/clients/${o.client_id}`} className="font-medium text-foreground hover:underline">{o.clients?.legal_name}</Link>{o.title ? ` · ${o.title}` : ""} · {dateShort(o.ordered_at)}
            {quote && <> · issue du devis <Link to={`/devis/${quote.id}`} className="hover:underline">{quote.number ?? "brouillon"}{quote.version > 1 ? ` v${quote.version}` : ""}</Link></>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {o.status === "pending" && <Button variant="outline" onClick={() => setConfirming(true)}>Confirmer manuellement</Button>}
          {["pending", "confirmed"].includes(o.status) && <Button variant="outline" className="text-destructive" onClick={() => setCancelling(true)}><XCircle className="mr-2 h-4 w-4" />Annuler</Button>}
          <DeleteButton entity="order" id={o.id} variant="outline" onDeleted={() => nav("/commandes")} />
        </div>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <section className="glass rounded-2xl p-5 lg:col-span-2">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Validation de la vente</h3>
          <ul className="space-y-2">
            <Step done label="Devis accepté" detail={quote ? `${quote.number ?? ""}` : undefined} />
            <Step done={Boolean(o.contract_signed_at)} label="Contrat signé" detail={o.contract_signed_at ? dateTime(o.contract_signed_at) : "Bloc Contrat ci-dessous (signature électronique ou hors ligne)"} />
            <Step done={Boolean(o.deposit_paid_at) || Number(o.deposit_pct) === 0} label={`Acompte encaissé (${Number(o.deposit_pct)} % — ${money(o.deposit_amount, o.currency)})`} detail={o.deposit_paid_at ? dateTime(o.deposit_paid_at) : Number(o.deposit_pct) === 0 ? "Aucun acompte demandé" : "Dès que la facture d'acompte est payée"} />
            <Step done={Boolean(o.confirmed_at)} label="Commande confirmée" detail={o.confirmed_at ? `${dateTime(o.confirmed_at)}${o.confirmation_note ? ` — forcée : ${o.confirmation_note}` : ""}` : "Automatique dès les deux conditions remplies"} />
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">Aucune livraison ne peut être enregistrée tant que la commande n'est pas confirmée.</p>
        </section>
        <section className="glass rounded-2xl p-5">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Conditions figées</h3>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Total</dt><dd className="font-semibold">{money(o.total, o.currency)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Acompte</dt><dd>{money(o.deposit_amount, o.currency)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Solde</dt><dd>{money(o.balance_amount, o.currency)} à {o.payment_days} j</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Régime fiscal</dt><dd>{o.tax_label ?? "—"} ({Number(o.tax_rate_pct)} %)</dd></div>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">Copiés du devis à la création : un changement de catalogue ou de tarif ne les modifie jamais.</p>
        </section>
      </div>

      <DocumentEditor doc={doc} onChange={() => undefined} clientId={o.client_id} readOnly />

      <OrderContractSection orderId={o.id} clientId={o.client_id} cancelled={o.status === "cancelled"} />
      <OrderBillingSection order={o} />

      {["confirmed", "in_delivery", "delivered", "closed"].includes(o.status) && (
        <DeliveriesSection orderId={o.id} clientChannel={o.clients?.delivery_channel ?? "manual"}
          canDeliver={["confirmed", "in_delivery", "delivered"].includes(o.status) && !o.delivery_suspended && o.clients?.status !== "suspended"}
          blockedReason={o.clients?.status === "suspended" ? "Les livraisons de ce client sont suspendues." : o.delivery_suspended ? "Les livraisons de cette commande sont suspendues." : undefined} />
      )}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirmer sans les deux conditions</DialogTitle><DialogDescription>Normalement la commande se confirme seule quand le contrat est signé et l'acompte encaissé. Forcer la confirmation est tracé avec ton nom et ton motif.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label htmlFor="confirm-note">Motif *</Label><Textarea id="confirm-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setConfirming(false)}>Annuler</Button><Button disabled={!note.trim()} onClick={() => update.mutate({ status: "confirmed", confirmation_note: note.trim() }, { onSuccess: () => { setConfirming(false); setNote(""); toast.success("Commande confirmée"); } })}>Confirmer</Button></div>
        </DialogContent>
      </Dialog>
      <Dialog open={cancelling} onOpenChange={setCancelling}>
        <DialogContent>
          <DialogHeader><DialogTitle>Annuler la commande {o.number}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label htmlFor="cancel-reason">Motif *</Label><Textarea id="cancel-reason" rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setCancelling(false)}>Retour</Button><Button variant="destructive" disabled={!note.trim()} onClick={() => update.mutate({ status: "cancelled", cancel_reason: note.trim() }, { onSuccess: () => { setCancelling(false); setNote(""); toast.success("Commande annulée"); } })}>Annuler la commande</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
